import React, { useEffect, useState } from "react";
import TornaAlMenu from "../components/TornaAlMenu";

const API_URL = process.env.REACT_APP_API_URL || "";

const formatDateIT = (value) => {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString("it-IT", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const points = (v) => Number(v || 0).toLocaleString("it-IT", { minimumFractionDigits: 0, maximumFractionDigits: 2 });

const cleanPointText = (value) => String(value || "").replace(/(\\d+)\\.00/g, "$1");

const buildPointLogView = (log) => {
  const raw = cleanPointText(log?.reason || "Movimento punti");
  const n = Number(log?.points || 0);
  const lower = raw.toLowerCase();

  if (lower.includes("premio consegnato") || lower.includes("riscatto premio")) {
    const name = raw.replace(/^Premio consegnato:\s*/i, "").replace(/^Riscatto premio:\s*/i, "").trim();
    return { icon: "🎁", title: name || "Premio riscattato", subtitle: "Premio consegnato", badge: "Premio", badgeClass: "bg-danger-subtle text-danger", amountClass: "text-danger", bg: "#fff7f7" };
  }

  if (lower.includes("ordine")) {
    const orderMatch = raw.match(/Ordine\s+(\d+)/i);
    const title = orderMatch ? `Ordine #${orderMatch[1]}` : "Ordine app";
    const subtitle = raw.replace(/^Ordine\s+\d+\s+pagato\s+con\s+wallet:\s*/i, "").replace(/=\s*[\d,.]+\s+punti\s*$/i, "").replace(/[\d,.]+\s+x\s+/gi, "").replace(/\s*\|\s*/g, " + ").trim() || "Acquisto confermato";
    return { icon: "🍷", title, subtitle, badge: "Acquisto", badgeClass: "bg-success-subtle text-success", amountClass: n < 0 ? "text-danger" : "text-success", bg: "#f7fff9" };
  }

  return { icon: n < 0 ? "➖" : "➕", title: raw, subtitle: n < 0 ? "Uscita punti" : "Accredito punti", badge: n < 0 ? "Uscita" : "Accredito", badgeClass: n < 0 ? "bg-danger-subtle text-danger" : "bg-success-subtle text-success", amountClass: n < 0 ? "text-danger" : "text-success", bg: n < 0 ? "#fff7f7" : "#f7fff9" };
};

export default function AppPointsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [redeemingId, setRedeemingId] = useState(null);
  const [rewardQr, setRewardQr] = useState(null);

  const load = async ({ keepQr = false, keepMsg = false } = {}) => {
    setLoading(true);
    setErr("");
    if (!keepMsg) setMsg("");
    if (!keepQr) setRewardQr(null);

    try {
      const token = localStorage.getItem("access");
      const res = await fetch(`${API_URL}/api/app/me/`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json?.detail || "Errore caricamento punti");

      setData(json);
    } catch (e) {
      setErr(e.message || "Errore");
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    if (!rewardQr?.token) return;

    const checkRewardStatus = async () => {
      try {
        const token = localStorage.getItem("access");
        const res = await fetch(`${API_URL}/api/app/reward-status/${rewardQr.token}/`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const json = await res.json().catch(() => ({}));
        if (!res.ok) return;

        if (json?.status === "CONFIRMED") {
          const rewardName = json?.reward?.name || rewardQr?.reward?.name || "premio";
          setRewardQr(null);
          setMsg(`✅ Premio consegnato con successo: ${rewardName}. Punti aggiornati.`);
          await load({ keepMsg: true });
        }

        if (json?.status === "EXPIRED" || json?.status === "CANCELLED") {
          setRewardQr(null);
          setErr(json?.status === "EXPIRED" ? "QR premio scaduto. Richiedi nuovamente il premio." : "Richiesta premio annullata.");
          await load({ keepMsg: true });
        }
      } catch (e) {
        // polling silenzioso: non disturbiamo il cliente se una singola verifica fallisce
      }
    };

    checkRewardStatus();
    const id = window.setInterval(checkRewardStatus, 5000);
    return () => window.clearInterval(id);
  }, [rewardQr?.token]);

  const redeemReward = async (reward) => {
    if (!reward?.id) return;

    const ok = window.confirm(`Vuoi richiedere il premio "${reward.name}"? I punti verranno scalati solo quando il punto vendita confermerà il QR.`);
    if (!ok) return;

    setRedeemingId(reward.id);
    setErr("");
    setMsg("");

    try {
      const token = localStorage.getItem("access");
      const res = await fetch(`${API_URL}/api/app/redeem-reward/`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ reward_id: reward.id }),
      });

      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(json?.detail || "Errore riscatto premio");
      }

      if (json?.mode === "QR_CONFIRMATION_REQUIRED") {
        setRewardQr(json);
        setMsg("QR premio creato. Mostralo al punto vendita per confermare il riscatto.");
      } else {
        setMsg(json?.detail || "Premio riscattato con successo.");
        await load({ keepMsg: true });
      }
    } catch (e) {
      setErr(e.message || "Errore riscatto premio");
    } finally {
      setRedeemingId(null);
    }
  };

  const storico = data?.storico || [];
  const premi = data?.premi || [];
  const prossimo = data?.prossimo_premio || null;
  const saldoPunti = data?.punti ?? data?.cliente?.loyalty_points ?? 0;

  return (
    <div className="container py-4" style={{ maxWidth: 760 }}>
      <TornaAlMenu to="/" />

      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2 className="m-0">🎁 Punti e premi</h2>
        <button className="btn btn-outline-dark rounded-pill px-3" onClick={load}>
          🔄 Aggiorna
        </button>
      </div>

      {loading && <p className="text-muted">Caricamento…</p>}

      {!loading && err && (
        <div className="alert alert-danger">{err}</div>
      )}

      {msg && <div className="alert alert-success">{msg}</div>}

      {rewardQr && (
        <div className="card shadow-lg border-0 rounded-5 mb-3 overflow-hidden">
          <div className="card-body p-4 text-center">
            <div className="small text-uppercase text-muted mb-2">Conferma premio</div>
            <h4 className="fw-bold mb-2">{rewardQr.reward?.name}</h4>
            <p className="text-muted mb-3">
              Mostra questo QR al punto vendita. I punti verranno scalati solo dopo la conferma del gestore.
            </p>

            <div className="bg-white border rounded-4 p-3 d-inline-block mb-3">
              <img
                alt="QR conferma premio"
                width="220"
                height="220"
                src={`https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(rewardQr.qr_url || "")}`}
              />
            </div>

            {rewardQr.pickup_code && (
              <div className="mb-3">
                <div className="small text-muted mb-1">
                  Oppure comunica questo codice al punto vendita
                </div>
                <div
                  className="fw-bold font-monospace"
                  style={{ fontSize: "2rem", letterSpacing: "0.25rem" }}
                >
                  {rewardQr.pickup_code}
                </div>
              </div>
            )}

            <div className="small text-muted">
              Scade: {rewardQr.expires_at ? new Date(rewardQr.expires_at).toLocaleString("it-IT") : "-"}
            </div>
          </div>
        </div>
      )}

      {!loading && !err && data && (
        <>
            <div className="card shadow-lg border-0 rounded-5 mb-3 overflow-hidden">
              <div
                className="card-body p-4"
                style={{
                  background: "linear-gradient(135deg, #5b0f1b 0%, #8b1e2d 52%, #211014 100%)",
                  color: "#fff"
                }}
              >
                <div className="small text-uppercase" style={{ letterSpacing: 1.2, opacity: 0.82 }}>
                  Punti disponibili
                </div>
                <div className="display-4 fw-bold">{points(saldoPunti)}</div>

                {prossimo && (
                  <div className="mt-3 mb-0 rounded-4 p-3" style={{ background: "rgba(255,255,255,0.14)", border: "1px solid rgba(255,255,255,0.14)" }}>
                    <div className="fw-bold">Prossimo premio</div>
                    <div>{prossimo.name}</div>
                    <div className="small" style={{ opacity: 0.88 }}>
                      Ti mancano {points(prossimo.mancano)} punti per raggiungere il prossimo traguardo. Poi deciderai tu se riscattare il premio o continuare a raccogliere punti.
                    </div>
                  </div>
                )}
              </div>
          </div>

          <div className="card shadow-sm border-0 rounded-5 mb-3">
            <div className="card-body p-4">
              <h4 className="mb-3">Premi disponibili</h4>

              {premi.length === 0 ? (
                <div className="alert alert-info mb-0">Nessun premio disponibile.</div>
              ) : (
                <div className="row g-3">
                  {premi.map((p) => {
                    const enough = Number(saldoPunti) >= Number(p.required_points || 0);
                    return (
                      <div className="col-12 col-md-4" key={p.id}>
                        <div className="border rounded-4 p-3 h-100" style={{ background: "#fff8f1" }}>
                          <div className="fw-bold">{p.name}</div>
                          <div className="small text-muted mb-2">
                            Richiede {points(p.required_points)} punti
                          </div>
                          {enough ? (
                            <button
                              type="button"
                              className="btn btn-sm btn-success"
                              disabled={redeemingId === p.id}
                              onClick={() => redeemReward(p)}
                            >
                              {redeemingId === p.id ? "Creo QR..." : "🎁 Richiedi premio"}
                            </button>
                          ) : (
                            <span className="badge bg-secondary rounded-pill px-3">Non ancora disponibile</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          <div className="card shadow-sm border-0 rounded-5">
            <div className="card-body p-4">
              <h4 className="mb-3">Storico punti</h4>

              {storico.length === 0 ? (
                <div className="alert alert-info mb-0">Nessun movimento punti registrato.</div>
              ) : (
                <div className="list-group list-group-flush">
                    {storico.map((s, idx) => {
                      const item = buildPointLogView(s);
                      const n = Number(s.points || 0);

                      return (
                        <div key={idx} className="list-group-item px-0 border-0 mb-2">
                          <div className="rounded-4 p-3" style={{ background: item.bg, border: "1px solid rgba(0,0,0,0.05)" }}>
                            <div className="d-flex justify-content-between gap-3 align-items-start">
                              <div className="d-flex gap-3">
                                <div style={{ fontSize: 26, lineHeight: 1 }}>{item.icon}</div>
                                <div>
                                  <div className="d-flex flex-wrap align-items-center gap-2 mb-1">
                                    <span className={`badge rounded-pill ${item.badgeClass}`}>{item.badge}</span>
                                    <span className="small text-muted">{formatDateIT(s.date)}</span>
                                  </div>
                                  <div className="fw-bold" style={{ fontSize: 17 }}>{item.title}</div>
                                  <div className="small text-muted mt-1">{item.subtitle}</div>
                                </div>
                              </div>

                              <div className={`fw-bold ${item.amountClass}`} style={{ fontSize: 22, whiteSpace: "nowrap" }}>
                                {n > 0 ? "+" : ""}{points(n)}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
